import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  BooleanDotRenderer,
  type Column,
  createDateRangeFilterValidators,
  DateRenderer,
  EmptyStation,
  type ExplorerDataProvider,
  filterToPostGraphileFilter,
  FilterTypes,
  MessageBar,
  NavigationExplorer,
  sortToPostGraphileOrderBy,
  transformRange,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  type IdpUserFilter,
  IdpUsersDocument,
  IdpUsersOrderBy,
  type IdpUsersQuery,
  type IdpUsersQueryVariables,
  UserStatus,
} from '../../../../generated/axauth-service';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';

type IdpUserData = NonNullable<IdpUsersQuery['filtered']>['nodes'][number];

export const StatusRenderer = (val: unknown): JSX.Element =>
  BooleanDotRenderer(val === UserStatus.Active);

export const ExclusionPatternRenderer = (val: unknown): JSX.Element => {
  return <div>{val === true ? 'Yes' : 'No'}</div>;
};

const EmailVerifiedRenderer = (val: unknown): JSX.Element =>
  BooleanDotRenderer(val);

const explorerColumns: Column<IdpUserData>[] = [
  { label: 'First Name', propertyName: 'firstName' },
  {
    label: 'Last Name',
    propertyName: 'lastName',
  },
  {
    label: 'Email Address',
    propertyName: 'email',
  },
  {
    label: 'Email Verified',
    propertyName: 'emailVerified',
    render: EmailVerifiedRenderer,
  },
  {
    label: 'Status',
    propertyName: 'status',
    render: StatusRenderer,
  },
  {
    label: 'Exclusion Pattern Matched',
    propertyName: 'isAffectedByExclusionPattern',
    render: ExclusionPatternRenderer,
  },
  { label: 'Created At', propertyName: 'createdAt', render: DateRenderer },
  {
    label: 'Last Modified At',
    propertyName: 'updatedAt',
    render: DateRenderer,
  },
];

export const Users: React.FC = () => {
  const { envId, userStoreId } = useParams<{
    envId: string;
    userStoreId: string;
  }>();

  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);

  // Check the status of axauth-service
  useEffect(() => {
    (async () => {
      const statusMessage = await getManagedServiceAvailabilityMessage(
        ManagedServiceId.AXAUTH_SERVICE,
        envId,
      );
      setServiceStatusMessage(statusMessage);
    })();
  }, [envId]);

  const dataProvider: ExplorerDataProvider<IdpUserData> = {
    loadData: async ({ pagingInformation, sorting, filters }) => {
      const envApolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
      );

      const idpUsers = await envApolloClient?.query<
        IdpUsersQuery,
        IdpUsersQueryVariables
      >({
        query: IdpUsersDocument,
        variables: {
          condition: {
            idpUserStoreId: userStoreId,
          },
          filter: filterToPostGraphileFilter<IdpUserFilter>(filters, {
            firstName: 'includesInsensitive',
            lastName: 'includesInsensitive',
            email: 'includesInsensitive',
            status: 'in',
            isAffectedByExclusionPattern: 'equalTo',
            createdAt: transformRange,
            updatedAt: transformRange,
          }),
          orderBy: sortToPostGraphileOrderBy(sorting, IdpUsersOrderBy),
          after: pagingInformation,
        },
        fetchPolicy: 'network-only',
      });

      const data = idpUsers?.data.filtered?.nodes ?? [];

      return {
        data,
        totalCount: idpUsers?.data.nonFiltered?.totalCount ?? 0,
        filteredCount: idpUsers?.data.filtered?.totalCount ?? 0,
        hasMoreData: idpUsers?.data.filtered?.pageInfo.hasNextPage ?? false,
        pagingInformation: idpUsers?.data.filtered?.pageInfo.endCursor,
      };
    },
  };

  const [createFromDateFilterValidator, createToDateFilterValidator] =
    createDateRangeFilterValidators<IdpUserData>();

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="Provisioned User Accounts">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <NavigationExplorer<IdpUserData>
          title="Provisioned User Accounts"
          stationKey="ProvisionedUserAccountsExplorer"
          columns={explorerColumns}
          dataProvider={dataProvider}
          calculateNavigateUrl={(item) => {
            return `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/provisionedUsers/${item.id}`;
          }}
          onCreateAction={`/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/provisionedUsers/create`}
          defaultSortOrder={{ column: 'updatedAt', direction: 'desc' }}
          filterOptions={[
            {
              label: 'First Name',
              property: 'firstName',
              type: FilterTypes.FreeText,
            },
            {
              label: 'Last Name',
              property: 'lastName',
              type: FilterTypes.FreeText,
            },
            {
              label: 'Email Address',
              property: 'email',
              type: FilterTypes.FreeText,
            },
            {
              label: 'Status',
              property: 'status',
              type: FilterTypes.Options,
              options: [
                {
                  label: 'Active',
                  value: UserStatus.Active,
                },
                {
                  label: 'Blocked',
                  value: UserStatus.Blocked,
                },
              ],
            },
            {
              label: 'Exclusion Pattern Matched',
              property: 'isAffectedByExclusionPattern',
              type: FilterTypes.Options,
              options: [
                {
                  label: 'Yes',
                  value: true,
                },
                {
                  label: 'No',
                  value: false,
                },
              ],
            },
            {
              label: 'Creation Period (From)',
              property: 'createdAt',
              type: FilterTypes.Date,
              onValidate: createFromDateFilterValidator('createdAt'),
            },
            {
              label: 'Creation Period (To)',
              property: 'createdAt',
              type: FilterTypes.Date,
              onValidate: createToDateFilterValidator('createdAt'),
            },
            {
              label: 'Modification Period (From)',
              property: 'updatedAt',
              type: FilterTypes.Date,
              onValidate: createFromDateFilterValidator('updatedAt'),
            },
            {
              label: 'Modification Period (To)',
              property: 'updatedAt',
              type: FilterTypes.Date,
              onValidate: createToDateFilterValidator('updatedAt'),
            },
          ]}
        />
      )}
    </>
  );
};

export const UsersCrumb: BreadcrumbResolver = () => 'User Accounts';
